import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { 
  LayoutDashboard, 
  CheckSquare, 
  AlertTriangle, 
  MessageSquare, 
  MapPin, 
  Moon,
  BarChart3,
  LogOut,
  User,
  Sparkles,
  Rocket,
  Menu,
  X,
  Mail,
  FileText,
  Pencil,
  Check,
  Building2,
  Plus,
  Trash2,
  ChevronDown,
  GraduationCap,
  BookOpen,
  ShieldCheck,
  Wallet,
  FileDown,
  Settings as SettingsIcon,
  Calendar as CalendarIcon
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { NotificationCenter } from "@/components/NotificationCenter";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const NAV_SECTIONS = [
  {
    label: "Daily",
    items: [
      { path: "/dashboard", label: "Command Center", icon: LayoutDashboard },
      { path: "/tasks", label: "Tasks", icon: CheckSquare },
      { path: "/calendar", label: "Calendar", icon: CalendarIcon },
    ],
  },
  {
    label: "Growth",
    items: [
      { path: "/leadership", label: "Leadership Academy", icon: BookOpen },
      { path: "/certprep", label: "Cert Prep", icon: GraduationCap },
      { path: "/weekly", label: "Weekly Review", icon: BarChart3 },
    ],
  },
  {
    label: "Execute",
    items: [
      { path: "/sprints", label: "90-Day Sprints", icon: Rocket },
      { path: "/communication", label: "Comm Studio", icon: FileText },
      { path: "/training", label: "Training Center", icon: GraduationCap },
      { path: "/ai-assistant", label: "Niko Assistant", icon: Sparkles },
    ],
  },
  {
    label: "Clinical Ops",
    items: [
      { path: "/rounds", label: "Rounds", icon: MapPin },
      { path: "/incidents", label: "Incidents", icon: AlertTriangle },
      { path: "/flash-meeting", label: "Flash Meeting", icon: MessageSquare },
      { path: "/shutdown", label: "Shutdown", icon: Moon },
    ],
  },
  {
    label: "Personal",
    items: [
      { path: "/budget", label: "Budget", icon: Wallet },
      { path: "/settings", label: "Settings", icon: SettingsIcon },
    ],
  },
];

export const Sidebar = ({ isOpen, onToggle }) => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [editName, setEditName] = useState("");
  const [saving, setSaving] = useState(false);
  
  // Facility state
  const [facilities, setFacilities] = useState([]);
  const [activeFacility, setActiveFacility] = useState(null);
  const [showFacilityDialog, setShowFacilityDialog] = useState(false);
  const [newFacility, setNewFacility] = useState({ name: "", short_name: "", role: "Infection Preventionist" });

  // Edit / delete facility state
  const [editingFacility, setEditingFacility] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", short_name: "", role: "" });
  const [savingFacility, setSavingFacility] = useState(false);
  const [deleteConfirmFacility, setDeleteConfirmFacility] = useState(null);

  useEffect(() => {
    fetchFacilities();
  }, []);

  const fetchFacilities = async () => {
    try {
      const res = await axios.get(`${API}/facilities`, { withCredentials: true });
      setFacilities(res.data || []);
      // Set active facility from localStorage or use primary
      const savedFacilityId = localStorage.getItem("active_facility_id");
      const primary = res.data?.find(f => f.is_primary);
      const saved = res.data?.find(f => f.facility_id === savedFacilityId);
      setActiveFacility(saved || primary || res.data?.[0] || null);
    } catch (error) {
      console.error("Error fetching facilities:", error);
    }
  };

  const switchFacility = (facility) => {
    setActiveFacility(facility);
    localStorage.setItem("active_facility_id", facility.facility_id);
    toast.success(`Switched to ${facility.short_name || facility.name}`);
  };

  // Open a weekly handoff PDF for the given facility (inline in a new tab so
  // Safari/iPad can render it natively; user then uses the browser's PDF
  // toolbar to print / save / AirDrop / email).
  const openHandoffPdf = (facility) => {
    const today = new Date();
    const iso = today.toISOString().split("T")[0];
    const token = localStorage.getItem("niko_session_token") || "";
    const url = `${API}/handoff/weekly-pdf?facility_id=${encodeURIComponent(
      facility.facility_id
    )}&week_start=${iso}${token ? `&token=${encodeURIComponent(token)}` : ""}`;
    window.open(url, "_blank", "noopener");
    toast.success(`Preparing handoff for ${facility.short_name || facility.name}…`);
  };

  const addFacility = async () => {
    if (!newFacility.name || !newFacility.short_name) {
      toast.error("Please fill in facility name and short name");
      return;
    }
    
    try {
      await axios.post(`${API}/facilities`, newFacility, { withCredentials: true });
      toast.success("Facility added!");
      setShowFacilityDialog(false);
      setNewFacility({ name: "", short_name: "", role: "Infection Preventionist" });
      fetchFacilities();
    } catch (error) {
      toast.error("Failed to add facility");
    }
  };

  // Open edit dialog for a facility — pre-fills the form with current values.
  const openEditFacility = (facility) => {
    setEditingFacility(facility);
    setEditForm({
      name: facility.name || "",
      short_name: facility.short_name || "",
      role: facility.role || "Infection Preventionist",
      units: Array.isArray(facility.units) && facility.units.length > 0
        ? facility.units.map(u => ({ name: u.name || "", description: u.description || "" }))
        : [],
    });
  };

  // Save edits via PATCH /facilities/{id}.
  const saveFacilityEdit = async () => {
    if (!editingFacility) return;
    if (!editForm.name.trim() || !editForm.short_name.trim()) {
      toast.error("Name and short name are required");
      return;
    }
    setSavingFacility(true);
    try {
      const payload = {
        name: editForm.name,
        short_name: editForm.short_name,
        role: editForm.role,
        units: (editForm.units || [])
          .filter(u => u.name && u.name.trim())
          .map(u => ({ name: u.name.trim(), description: (u.description || "").trim() || null })),
      };
      await axios.patch(
        `${API}/facilities/${editingFacility.facility_id}`,
        payload,
        { withCredentials: true },
      );
      toast.success("Facility updated");
      // Optimistic local update so the dropdown reflects immediately
      setFacilities(prev => prev.map(f =>
        f.facility_id === editingFacility.facility_id ? { ...f, ...payload } : f
      ));
      if (activeFacility?.facility_id === editingFacility.facility_id) {
        setActiveFacility({ ...activeFacility, ...payload });
      }
      setEditingFacility(null);
      fetchFacilities();
    } catch (error) {
      console.error("Edit facility failed", error);
      toast.error("Couldn't update facility");
    } finally {
      setSavingFacility(false);
    }
  };

  // Remove a facility after confirming via the delete-confirm dialog.
  const confirmDeleteFacility = async () => {
    if (!deleteConfirmFacility) return;
    setSavingFacility(true);
    try {
      await axios.delete(
        `${API}/facilities/${deleteConfirmFacility.facility_id}`,
        { withCredentials: true },
      );
      toast.success(`Removed ${deleteConfirmFacility.short_name || deleteConfirmFacility.name}`);
      // If the active facility was the one deleted, clear it
      if (activeFacility?.facility_id === deleteConfirmFacility.facility_id) {
        localStorage.removeItem("active_facility_id");
        setActiveFacility(null);
      }
      setDeleteConfirmFacility(null);
      setEditingFacility(null);
      fetchFacilities();
    } catch (error) {
      console.error("Delete facility failed", error);
      toast.error(error?.response?.data?.detail || "Couldn't delete facility");
    } finally {
      setSavingFacility(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      // Clear the localStorage token used by Safari-friendly Bearer auth
      localStorage.removeItem("niko_session_token");
      toast.success("Logged out successfully");
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
      localStorage.removeItem("niko_session_token");
      navigate("/login");
    }
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const openProfileDialog = () => {
    setEditName(user?.name || "");
    setShowProfileDialog(true);
  };

  const saveProfile = async () => {
    if (!editName.trim()) {
      toast.error("Name cannot be empty");
      return;
    }
    
    setSaving(true);
    try {
      await axios.patch(`${API}/auth/profile`, { name: editName.trim() }, { withCredentials: true });
      toast.success("Profile updated!");
      setShowProfileDialog(false);
      // Refresh user data
      if (refreshUser) {
        refreshUser();
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleNavClick = () => {
    // Close sidebar on mobile after navigation
    if (window.innerWidth < 1024) {
      onToggle?.();
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={onToggle}
        />
      )}
      
      {/* Sidebar */}
      <aside 
        data-testid="sidebar"
        className={`fixed left-0 top-0 h-full w-64 bg-zinc-950 border-r border-zinc-800 flex flex-col z-50 no-print transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
      >
        {/* Logo */}
        <div className="p-4 lg:p-6 border-b border-zinc-800 flex items-center justify-between">
          <div>
            <h1 className="text-lg lg:text-xl font-bold tracking-tight flex items-center gap-2">
              <span className="w-7 h-7 lg:w-8 lg:h-8 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-white text-sm font-bold shadow-lg">
                N
              </span>
              <span className="hidden sm:inline bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text text-transparent">Niko OS</span>
              <span className="sm:hidden bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text text-transparent">Niko</span>
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {/* Notification Bell */}
            <NotificationCenter />
            {/* Close button on mobile */}
            <button 
              onClick={onToggle}
              className="lg:hidden p-2 rounded-lg hover:bg-zinc-800"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Facility Switcher */}
        <div className="px-3 py-2 border-b border-zinc-800">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-2 p-2 rounded-lg bg-zinc-900/50 hover:bg-zinc-800/50 transition-colors">
                <Building2 className="w-4 h-4 text-primary" />
                <div className="flex-1 text-left min-w-0">
                  {activeFacility ? (
                    <>
                      <p className="text-xs font-medium truncate">{activeFacility.short_name || activeFacility.name}</p>
                      <p className="text-[10px] text-muted-foreground truncate">{activeFacility.role}</p>
                    </>
                  ) : (
                    <p className="text-xs text-muted-foreground">Select Facility</p>
                  )}
                </div>
                <ChevronDown className="w-3 h-3 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64">
              {facilities.map((facility) => (
                <div
                  key={facility.facility_id}
                  className="flex items-center gap-1 px-1 py-0.5 rounded hover:bg-zinc-800/40"
                >
                  <button
                    type="button"
                    onClick={() => switchFacility(facility)}
                    className="flex flex-1 items-center gap-2 px-2 py-1.5 rounded text-left min-w-0"
                    data-testid={`switch-facility-${facility.facility_id}`}
                  >
                    <Building2 className="w-4 h-4 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">{facility.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{facility.role}</p>
                    </div>
                    {facility.is_primary && (
                      <Badge variant="outline" className="text-[10px] flex-shrink-0">Primary</Badge>
                    )}
                    {activeFacility?.facility_id === facility.facility_id && (
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      openHandoffPdf(facility);
                    }}
                    className="p-1.5 rounded text-muted-foreground hover:text-cyan-400 hover:bg-cyan-500/10
                               focus:outline-none focus:ring-1 focus:ring-cyan-500"
                    aria-label={`Handoff PDF for ${facility.short_name || facility.name}`}
                    data-testid={`handoff-facility-${facility.facility_id}`}
                    title="Weekly handoff PDF"
                  >
                    <FileDown className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      openEditFacility(facility);
                    }}
                    className="p-1.5 rounded text-muted-foreground hover:text-primary hover:bg-zinc-800
                               focus:outline-none focus:ring-1 focus:ring-violet-500"
                    aria-label={`Edit ${facility.short_name || facility.name}`}
                    data-testid={`edit-facility-${facility.facility_id}`}
                    title="Edit this facility"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setDeleteConfirmFacility(facility);
                    }}
                    className="p-1.5 rounded text-muted-foreground hover:text-red-400 hover:bg-red-500/10
                               focus:outline-none focus:ring-1 focus:ring-red-500"
                    aria-label={`Remove ${facility.short_name || facility.name}`}
                    data-testid={`remove-facility-${facility.facility_id}`}
                    title="Remove this facility"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setShowFacilityDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Facility
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 lg:p-4 overflow-y-auto">
          {NAV_SECTIONS.map((section, sIdx) => (
            <div key={section.label} className={sIdx === 0 ? "" : "mt-5"}>
              <div className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                {section.label}
              </div>
              <div className="space-y-1">
                {section.items.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={handleNavClick}
                    data-testid={`nav-${item.path.slice(1)}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200
                      ${isActive
                        ? "sidebar-item-active"
                        : "text-muted-foreground hover:text-foreground hover:bg-zinc-800/50"
                      }`
                    }
                  >
                    <item.icon className="w-5 h-5 flex-shrink-0" />
                    <span className="truncate">{item.label}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          ))}

          {/* Owner-only: Review Dashboard for closed-beta management */}
          {user?.is_owner && (
            <div className="mt-5">
              <div className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-violet-500">
                Owner
              </div>
              <NavLink
                to="/review"
                onClick={handleNavClick}
                data-testid="nav-review"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200
                  ${isActive
                    ? "sidebar-item-active"
                    : "text-muted-foreground hover:text-foreground hover:bg-zinc-800/50"
                  }`
                }
              >
                <ShieldCheck className="w-5 h-5 flex-shrink-0" />
                <span className="truncate">Review Dashboard</span>
              </NavLink>
            </div>
          )}
        </nav>

        {/* User Profile */}
        <div className="p-3 lg:p-4 border-t border-zinc-800">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button 
                data-testid="user-menu-trigger"
                className="flex items-center gap-3 w-full p-2 rounded-lg hover:bg-zinc-800/50 transition-colors"
              >
                <Avatar className="w-8 h-8 flex-shrink-0">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="bg-primary/20 text-primary text-xs">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left min-w-0">
                  <p className="text-sm font-medium truncate">{user?.name || "User"}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem 
                className="flex items-center gap-2"
                onClick={openProfileDialog}
              >
                <Pencil className="w-4 h-4" />
                Edit Name
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <a href="mailto:support@nikosafe.com" className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Contact Support
                </a>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                data-testid="logout-btn"
                onClick={handleLogout}
                className="flex items-center gap-2 text-red-400 focus:text-red-400"
              >
                <LogOut className="w-4 h-4" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        
        {/* Niko OS Branding */}
        <div className="px-4 py-3 border-t border-zinc-800 text-center">
          <p className="text-[10px] text-muted-foreground">
            Powered by <span className="font-medium bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text text-transparent">Niko OS</span>
          </p>
        </div>
      </aside>

      {/* Profile Edit Dialog */}
      <Dialog open={showProfileDialog} onOpenChange={setShowProfileDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Edit Profile
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <Avatar className="w-16 h-16">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback className="bg-primary/20 text-primary text-lg">
                  {getInitials(user?.name)}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Display Name</label>
              <Input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="Enter your name"
                onKeyDown={(e) => e.key === 'Enter' && saveProfile()}
              />
            </div>
            
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowProfileDialog(false)}>
                Cancel
              </Button>
              <Button onClick={saveProfile} disabled={saving}>
                {saving ? (
                  "Saving..."
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Save
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Facility Dialog */}
      <Dialog open={showFacilityDialog} onOpenChange={setShowFacilityDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Add Facility
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Facility Name *</Label>
              <Input
                value={newFacility.name}
                onChange={(e) => setNewFacility({ ...newFacility, name: e.target.value })}
                placeholder="e.g., St. Mary's Hospital"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Short Name *</Label>
              <Input
                value={newFacility.short_name}
                onChange={(e) => setNewFacility({ ...newFacility, short_name: e.target.value })}
                placeholder="e.g., SMH"
              />
              <p className="text-xs text-muted-foreground">Used for quick display in the sidebar</p>
            </div>
            
            <div className="space-y-2">
              <Label>Your Role</Label>
              <Select
                value={newFacility.role}
                onValueChange={(value) => setNewFacility({ ...newFacility, role: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Infection Preventionist">Infection Preventionist</SelectItem>
                  <SelectItem value="Quality Manager">Quality Manager</SelectItem>
                  <SelectItem value="Director of Nursing">Director of Nursing</SelectItem>
                  <SelectItem value="Chief Nursing Officer">Chief Nursing Officer</SelectItem>
                  <SelectItem value="Risk Manager">Risk Manager</SelectItem>
                  <SelectItem value="Patient Safety Officer">Patient Safety Officer</SelectItem>
                  <SelectItem value="Consultant">Consultant</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowFacilityDialog(false)}>
                Cancel
              </Button>
              <Button onClick={addFacility}>
                <Plus className="w-4 h-4 mr-2" />
                Add Facility
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Facility Dialog */}
      <Dialog open={!!editingFacility} onOpenChange={(open) => !open && setEditingFacility(null)}>
        <DialogContent className="sm:max-w-md" data-testid="edit-facility-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5" />
              Edit Facility
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Facility Name *</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                placeholder="e.g., St. Mary's Hospital"
                data-testid="edit-facility-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Short Name *</Label>
              <Input
                value={editForm.short_name}
                onChange={(e) => setEditForm({ ...editForm, short_name: e.target.value })}
                placeholder="e.g., SMH"
                data-testid="edit-facility-short-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Your Role</Label>
              <Select
                value={editForm.role}
                onValueChange={(value) => setEditForm({ ...editForm, role: value })}
              >
                <SelectTrigger data-testid="edit-facility-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Infection Preventionist">Infection Preventionist</SelectItem>
                  <SelectItem value="Quality Manager">Quality Manager</SelectItem>
                  <SelectItem value="Director of Nursing">Director of Nursing</SelectItem>
                  <SelectItem value="Chief Nursing Officer">Chief Nursing Officer</SelectItem>
                  <SelectItem value="Risk Manager">Risk Manager</SelectItem>
                  <SelectItem value="Patient Safety Officer">Patient Safety Officer</SelectItem>
                  <SelectItem value="Consultant">Consultant</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Rounding Units editor */}
            <div className="space-y-2 pt-2 border-t border-zinc-800">
              <div className="flex items-center justify-between">
                <Label>Rounding Units</Label>
                <button
                  type="button"
                  onClick={() => setEditForm({
                    ...editForm,
                    units: [...(editForm.units || []), { name: "", description: "" }],
                  })}
                  className="text-xs text-violet-400 hover:text-violet-300 inline-flex items-center gap-1"
                  data-testid="edit-add-unit-btn"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add unit
                </button>
              </div>
              {(editForm.units || []).length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  No units configured. Add the units you intentionally round on so they
                  show up on the Rounds page.
                </p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {(editForm.units || []).map((u, idx) => (
                    <div key={idx} className="flex gap-1.5 items-center">
                      <Input
                        value={u.name}
                        onChange={(e) => {
                          const next = [...editForm.units];
                          next[idx] = { ...next[idx], name: e.target.value };
                          setEditForm({ ...editForm, units: next });
                        }}
                        placeholder="Unit 300"
                        className="flex-1 h-9 text-sm"
                        data-testid={`edit-unit-name-${idx}`}
                      />
                      <Input
                        value={u.description}
                        onChange={(e) => {
                          const next = [...editForm.units];
                          next[idx] = { ...next[idx], description: e.target.value };
                          setEditForm({ ...editForm, units: next });
                        }}
                        placeholder="Adult"
                        className="flex-1 h-9 text-sm"
                        data-testid={`edit-unit-desc-${idx}`}
                      />
                      <button
                        type="button"
                        onClick={() => setEditForm({
                          ...editForm,
                          units: editForm.units.filter((_, i) => i !== idx),
                        })}
                        className="p-1.5 rounded text-muted-foreground hover:text-red-400 hover:bg-red-500/10 flex-shrink-0"
                        aria-label="Remove unit"
                        data-testid={`edit-remove-unit-${idx}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-2 pt-2">
              <Button
                variant="ghost"
                onClick={() => setDeleteConfirmFacility(editingFacility)}
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                disabled={savingFacility}
                data-testid="delete-facility-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Remove
              </Button>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setEditingFacility(null)}
                  disabled={savingFacility}
                >
                  Cancel
                </Button>
                <Button
                  onClick={saveFacilityEdit}
                  disabled={savingFacility}
                  data-testid="save-facility-edit-btn"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Save
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteConfirmFacility}
        onOpenChange={(open) => !open && setDeleteConfirmFacility(null)}
      >
        <DialogContent className="sm:max-w-md" data-testid="delete-facility-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <Trash2 className="w-5 h-5" />
              Remove Facility?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              You&apos;re about to remove{" "}
              <span className="font-semibold text-foreground">
                {deleteConfirmFacility?.name}
              </span>
              . This won&apos;t delete its meetings, incidents, or rounds — those stay attached to your account.
              You can re-add it later if needed.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setDeleteConfirmFacility(null)}
                disabled={savingFacility}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmDeleteFacility}
                disabled={savingFacility}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-facility-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Remove
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Mobile Header Component
export const MobileHeader = ({ onMenuClick }) => {
  return (
    <header className="fixed top-0 left-0 right-0 h-14 bg-zinc-950 border-b border-zinc-800 flex items-center px-4 z-40 lg:hidden">
      <button 
        onClick={onMenuClick}
        className="p-2 rounded-lg hover:bg-zinc-800 mr-3"
        aria-label="Open menu"
      >
        <Menu className="w-5 h-5" />
      </button>
      <h1 className="text-lg font-bold flex items-center gap-2">
        <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-white text-sm font-bold shadow-lg">
          N
        </span>
        <span className="bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text text-transparent">Niko OS</span>
      </h1>
    </header>
  );
};
